import React, { useEffect, useState } from 'react';
import { AppInputField } from '../../general/AppInputField/AppInputField';
import { DillModal, DillButton } from '../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { getUserDetails, updateUserProfile } from '../../../redux/services/authService';
import { openMessageModal } from '../../../redux/globalSlices/genericSlice';

interface EditProfileModalProps {
  open: boolean;
  handleClose: (editSuccess?: boolean) => void;
}

function EditProfileModal({ open, handleClose }: EditProfileModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  useEffect(() => {
    if (open) {
      setErrors({
        firstName: '',
        lastName: '',
        phoneNumber: '',
      });
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setTitle(user.title || '');
      setPhoneNumber(user.phoneNumber || '');
    }
  }, [user]);

  const validateInputs = () => {
    const newErrors = {
      firstName: '',
      lastName: '',
      phoneNumber: '',
    };

    let isValid = true;

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required.';
      isValid = false;
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleEditProfile = async () => {
    if (!validateInputs()) {
      return;
    }

    setIsLoading(true);

    const profileDetails = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      title: title.trim() || undefined,
      phoneNumber: phoneNumber.trim() || undefined,
    };

    const resp = await dispatch(
      updateUserProfile({
        profileDetails,
      })
    );

    setIsLoading(false);

    if (resp.type === 'auth/updateUserProfile/fulfilled') {
      await dispatch(getUserDetails());
      handleClose(true);
    } else {
      const payload: string = resp.payload;

      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error updating profile',
          subTitle: payload,
          onClose: () => {
            // handleClose();
          },
        })
      );
    }
  };

  return (
    <DillModal
      open={open}
      onClose={() => handleClose()}
      title="Edit Profile"
      maxWidth="520px"
      footer={
        <>
          <DillButton
            label="Cancel"
            variant="secondary"
            isDisabled={isLoading}
            onClick={() => handleClose()}
          />
          <DillButton
            label="Save Changes"
            onClick={handleEditProfile}
            isLoading={isLoading}
            width="120px"
            className="flex justify-center"
          />
        </>
      }>
      <div className="flex flex-row gap-2">
        <div className="w-full">
          <AppInputField
            label="First Name"
            placeholder="Enter first name"
            isRequired={true}
            value={firstName}
            errorText={errors.firstName}
            onTextChange={(text) => setFirstName(text)}
          />
        </div>
        <div className="w-full">
          <AppInputField
            label="Last Name"
            placeholder="Enter last name"
            isRequired={true}
            value={lastName}
            errorText={errors.lastName}
            onTextChange={(text) => setLastName(text)}
          />
        </div>
      </div>
      <AppInputField
        label="Job Title"
        placeholder="Enter job title"
        value={title}
        onTextChange={(text) => setTitle(text)}
      />
      <AppInputField
        label="Phone Number"
        placeholder="Enter phone number"
        inputType="tel"
        value={phoneNumber}
        errorText={errors.phoneNumber}
        onTextChange={(text) => setPhoneNumber(text)}
      />
    </DillModal>
  );
}

export default EditProfileModal;
